import React from 'react';
import { Action, ActionImpl, KBarResults, useMatches } from 'kbar';
import { CommandBarResultsItem } from './CommandBarResultsItem';
import { UserResultItem } from './UserResultItem';
import { RenderParams } from './commabarTypes';

const HiddenCommandBarActionIds = {
  'escape-shortcut': true,
  'search-shortcut': true,
  shortcutSidebar: true,
  'sidebar-payments': true,
};

/**
 * check the type of the result and return Id accordingly
 * @param result the result from Kbar useMatches
 * @returns
 */
const getResultItemId = (result: ActionImpl | string) => {
  if (typeof result === 'string' || result instanceof String) {
    return result as string;
  }
  return result.id;
};

export const CommandBarResult: React.FC = () => {
  const { results, rootActionId } = useMatches();

  const filterBySectionName = (
    action: ActionImpl | string,
    sectionName: string,
  ) => action === sectionName || (action as ActionImpl).section === sectionName;

  /**
   * This holds commandbar actions search results
   * to be shown in the command bar menu.
   * It filters out the actions we don't want to show in the command bar.
   * Also it sort the actions by their section name.
   */
  const commandBarItems = React.useMemo(() => {
    const stagedResults = results.filter(
      result => !(getResultItemId(result) in HiddenCommandBarActionIds),
    );

    const navigationCommands = stagedResults.filter(item =>
      filterBySectionName(item, 'Navigation'),
    );
    const generalCommands = stagedResults.filter(item =>
      filterBySectionName(item, 'General'),
    );
    const helpCommands = stagedResults.filter(item =>
      filterBySectionName(item, 'Help'),
    );
    const miscellaneousCommands = stagedResults.filter(item =>
      filterBySectionName(item, 'Miscellaneous'),
    );
    const accountCommands = stagedResults.filter(item =>
      filterBySectionName(item, 'Account'),
    );
    const otherCommands = stagedResults.filter(
      item =>
        // group actions objects without section prop
        typeof item !== 'string' && !(item instanceof String) && !item.section,
    );

    // commands should appear in a specific order
    return [
      ...otherCommands,
      ...generalCommands,
      ...navigationCommands,
      ...helpCommands,
      ...miscellaneousCommands,
      ...accountCommands,
    ];
  }, [results, rootActionId]);

  const onItemRender = React.useCallback((props: RenderParams) => {
    // when item has parent id equal to open-client
    // it should render item with avatar
    if ((props.item as Action).parent === 'open-client') {
      return <UserResultItem {...props} />;
    }

    return <CommandBarResultsItem {...props} />;
  }, []);

  return <KBarResults items={commandBarItems} onRender={onItemRender} />;
};
